import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { LogOut, UserPlus } from "react-feather";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { WalletIcon } from "@solana/wallet-adapter-react-ui";
import { SolanaMobileWalletAdapterWalletName } from "@solana-mobile/wallet-adapter-mobile";
import { IWalletInfo } from "@axvdex/state/wallet/initialState";
import { postFaucet } from "@axvdex/api/user";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import useLanguage from "@axvdex/hooks/useLanguage";
import { requestSolanaTestToken } from "@axvdex/hooks/solana/useTestToken";
import { useUserBalanceContext } from "@axvdex/hooks/solana/useUserBalance";
import { ReactComponent as LogoSolana, ReactComponent as SolTokenIcon } from "../../assets/logos/logo-solana.svg";

import { useGlobalModalContext } from "../../providers/GlobalModalProvider";
import Button from "../common/Button";
import styles from "../../styles/UserWallet.module.scss";

import { ReactComponent as LogoCosmostation } from "../../assets/logos/logo-wallet-cosmostation.svg";
import { ReactComponent as LogoKeplr } from "../../assets/logos/logo-wallet-keplr.svg";
import { ReactComponent as LogoLeap } from "../../assets/logos/logo-wallet-leap.svg";
import { ReactComponent as FaucetConstIcon } from "../../assets/tokens/logo-arch.svg";
import { ReactComponent as TestTokenIcon } from "../../assets/tokens/logo-test.svg";
import NotificationsModal from "../modals/NotificationsModal";
import UserSettingsModal from "../modals/UserSettingsModal";
import { useAppDispatch } from "state";
import { disconnectWallet, updateNativeBalance } from "state/wallet/walletThunks";
import { loadState, WHITE_LIST_PERSISTED_STATE_KEYS } from "state/persist";
import { Height } from "secretjs/dist/protobuf/ibc/core/client/v1/client";

interface UserWalletProps {
  hideInfo?: boolean;
  walletInfo: IWalletInfo;
  onClick: () => void;
}

function UserWallet({ hideInfo, walletInfo, onClick: onClickToToggleSideMenu }: UserWalletProps) {
  const dispatch = useAppDispatch();
  const { i18 } = useLanguage();
  const { disconnect } = useWallet();

  // const walletInfo = useAppSelector(selectWalletInfo);
  const [isFaucetLoading, setIsFaucetLoading] = useState(false);
  const [isUserConnected, setIsUserConnected] = useState(!!walletInfo.isConnected);

  const handleConnectWallet = useCallback(async () => {
    if (!loadState(WHITE_LIST_PERSISTED_STATE_KEYS.agreedCompliance)) {
      showModal("complianceModal", {
        connectWalletAfter: true,
      });
    } else {
      showModal("walletSelectionModal");
    }
  }, []);

  const handleDisconnectWallet = useCallback(async () => {
    if (process.env.REACT_APP_SOLANA_ENABLED === "1") {
      disconnect().catch(() => {
        // Silently catch because any errors are caught by the context `onError` handler
      });
    }
    await dispatch(disconnectWallet());
  }, []);

  const { showModal } = useGlobalModalContext();

  useEffect(() => {
    setIsUserConnected(!!walletInfo.isConnected);
  }, [walletInfo.isConnected]);

  const { connection } = useConnection();
  const { triggerFetchUserAssetBalances } = useUserBalanceContext();
  const { sendTransaction } = useWallet();

  const handleSolanaUseTestFaucet = async () => {
    try {
      setIsFaucetLoading(true);
      const balance = await requestSolanaTestToken(connection, 100, sendTransaction);
      triggerFetchUserAssetBalances();
      dispatch(
        sendToast({
          type: "tx-success",
          info: { msg: `You have ${balance} test token`, toastID: "" + new Date().getTime() },
        })
      );
    } catch (err) {
      console.log(err);
      const message = err.message || "An error occurred";
      dispatch(
        sendToast({
          type: "tx-fail",
          info: {
            msg: message,
            toastID: "" + new Date().getTime(),
          },
        })
      );
    } finally {
      setIsFaucetLoading(false);
    }
  };

  const handleUseTestFaucet = async () => {
    let err;

    try {
      setIsFaucetLoading(true);
      if (process.env.REACT_APP_SOLANA_ENABLED === "1") {
        await requestSolanaTestToken(connection, 100, sendTransaction);
        triggerFetchUserAssetBalances();
      } else {
        const res = await postFaucet({ address: walletInfo.walletAddress }, {});
        console.log(res.err.error);
        if (res.ok) {
          dispatch(
            sendToast({
              type: "tx-success",
              info: { msg: "Got 10.00 CONST from faucet", toastID: "" + new Date().getTime() },
            })
          );
          await dispatch(
            updateNativeBalance({
              userAddress: walletInfo.walletAddress,
              denom: process.env.REACT_APP_ARCHWAY_MINIMAL_DENOM,
            })
          );
        } else {
          err = res.err.error;
        }
      }
    } catch (err) {
      /* empty */
    }

    if (err) {
      if (err.message.includes("already transferred")) {
        dispatch(
          sendToast({
            type: "tx-fail",
            info: {
              msg: "Failed to get CONST from faucet. Reason: Quota exceeded!",
              toastID: "" + new Date().getTime(),
            },
          })
        );
      } else {
        dispatch(
          sendToast({
            type: "tx-fail",
            info: {
              msg: "Failed to get CONST from faucet. Reason: " + err.message,
              toastID: "" + new Date().getTime(),
            },
          })
        );
      }
    }
    setIsFaucetLoading(false);
  };

  const walletKind = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet);
  let walletName = walletKind.charAt(0).toUpperCase() + walletKind.slice(1);
  const { wallet } = useWallet();
  if (wallet && wallet.adapter.name === SolanaMobileWalletAdapterWalletName) {
    // HACK: [Android only] Pass the wallet name to the view
    console.log("Wallet name: ", window.walletName);
    walletName = window.walletName || wallet.adapter.name;
  }

  return (
    <div className={clsx(styles.userWallet, "userWallet")}>
      {isUserConnected ? (
        <>
          <div className="userWalletWrapper">
            <div
              className={clsx(
                "userWalletAvatar",
                process.env.REACT_APP_OUTBID_ONLY !== "1" &&
                  !loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits)[
                  process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress
                  ]
                  ? "isClickableWithRed"
                  : "isClickable"
              )}
              onClick={() => {
                if (
                  !loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits)[
                  process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress
                  ]
                ) {
                  showModal("permitAuthenticationModal");
                } else {
                  onClickToToggleSideMenu();
                }
              }}
              title={
                !loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits)[
                  process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress
                ]
                  ? i18("Create Permit is required", "userWallet.createPermitRequired")
                  : i18(`${walletName} wallet connected`, "userWallet.connected.title", { walletName })
              }
            >
              {/* Badge when permit has not been created yet */}
              {process.env.REACT_APP_OUTBID_ONLY !== "1" &&
                process.env.REACT_APP_SOLANA_ENABLED !== "1" &&
                !loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits)[
                process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress
                ] && (
                  <span className="badge red">
                    <span className="badgeIcon">!</span>
                  </span>
                )}
              <UserWalletLogo walletInfo={walletInfo} />
            </div>

            {!hideInfo && (
              <div
                className="userWalletInfo"
                title={i18(`Copy address to clipboard`, "userWallet.copyToClipboard.title")}
                onClick={() => {
                  // copy to clipboard
                  navigator.clipboard.writeText(walletInfo.walletAddress);
                  dispatch(
                    sendToast({
                      type: "info",
                      noStore: true,
                      info: {
                        msg: i18("Address copied to clipboard", "toast.addressCopied.text"),
                        toastID: "" + new Date().getTime(),
                      },
                    })
                  );
                }}
              >
                <div className="userWalletName">
                  {wallet && wallet.adapter && wallet.adapter.name === SolanaMobileWalletAdapterWalletName
                    ? // HACK: [Android only] Pass the wallet name to the view
                    walletName
                    : walletInfo.walletOwnerName}
                  &nbsp;{}
                </div>

                <div className="userWalletId">
                  <span>{walletInfo.walletAddress.slice(0, -4)}</span>
                  <span>{walletInfo.walletAddress.slice(-4)}</span>
                </div>
              </div>
            )}

            {!hideInfo &&
              loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits)[
              process.env.REACT_APP_ARCHWAY_NETWORK + "_" + walletInfo.walletAddress
              ] ? (
              <div style={{ width: "3em" }}>
                <UserSettingsModal />
                <Button
                  style={{ height: "2em" }}
                  extraClassName="btnDisconnectWallet"
                  title={i18(`Disconnect ${walletName} wallet`, "userWallet.disconnectBtn.title", { walletName })}
                  btnVariant="icon"
                  icon={<LogOut />}
                  onClick={handleDisconnectWallet}
                />
              </div>
            ) : (
              <div style={{ width: "3em" }}>
                <UserSettingsModal />
                <Button
                  style={{ height: "2em" }}
                  extraClassName="btnDisconnectWallet"
                  title={i18(`Disconnect ${walletName} wallet`, "userWallet.disconnectBtn.title", { walletName })}
                  btnVariant="icon"
                  icon={<LogOut />}
                  onClick={handleDisconnectWallet}
                />
              </div>
            )}
            {/* <div style={{ width: "3em" }}>
              <Button 
                style={{ height: "2em" }}
                title="User Settings" 
                aria-label="User Settings" 
                data-variant="icon" 
              >
              </Button>
            </div> */}
          </div>
          <NotificationsModal hideInfo={hideInfo} />
          {process.env.REACT_APP_MODE !== "MAINNET" && process.env.REACT_APP_SOLANA_ENABLED !== "1" && (
            <Button
              disabled={isFaucetLoading}
              extraClassName="btnFaucet"
              btnSize="sm"
              title={i18("Claim 10.00 CONST Faucet", "userWallet.faucet.title")}
              icon={
                <span className={"faucetIcon"}>
                  <FaucetConstIcon />
                </span>
              }
              onClick={event => {
                event.preventDefault();
                handleUseTestFaucet();
              }}
            >
              {!hideInfo && <>{i18("10.00 CONST Faucet", "userWallet.faucet.text")}</>}
            </Button>
          )}

          {process.env.REACT_APP_MODE !== "MAINNET" && process.env.REACT_APP_SOLANA_ENABLED === "1" && (
            <>
              <Button
                disabled={isFaucetLoading}
                extraClassName="btnFaucet"
                btnSize="sm"
                title={i18("Faucet: Test Token", "userWallet.faucet.title")}
                icon={
                  <span className={"faucetIcon"}>
                    <TestTokenIcon />
                  </span>
                }
                onClick={event => {
                  event.preventDefault();
                  handleSolanaUseTestFaucet();
                }}
              >
                {!hideInfo && <>{i18("Faucet: Test Token", "userWallet.faucet.title")}</>}
              </Button>
              <Button
                extraClassName="btnFauct"
                btnSize="sm"
                title={i18("Faucet: SOL", "userWallet.faucet.title")}
                icon={
                  <span className={"faucetIcon"}>
                    <SolTokenIcon />
                  </span>
                }
                onClick={event => {
                  event.preventDefault();
                  const net = connection.rpcEndpoint.toLowerCase().includes("devnet") ? "devnet" : "testnet";
                  const url = `https://faucet.quicknode.com/solana/${net}`;
                  window.open(url, "_blank");
                }}
              >
                {!hideInfo && <>{i18("Faucet: SOL", "userWallet.faucet.title")}</>}
              </Button>
            </>
          )}

          {/* <Button
            extraClassName="btnCollectRewards"
            btnSize="sm"
            btnColor="gradient"
            title={i18("Collect Rewards", "userWallet.collectReward.title")}
            icon={<AstrovaultTokenIcon />}
            onClick={handleOpenRewardsModal}
          >
            {!hideInfo && (
              <>
                {i18("Collect", "userWallet.collectReward.text")} {DummyUserInfo.userRewardsAmount}
              </>
            )}
          </Button> */}
        </>
      ) : (
        <Button
          extraClassName="btnConnectWallet"
          btnColor="gradient"
          title={i18("Connect Your Wallet", "userWallet.connectBtn.title")}
          text={hideInfo ? "" : i18("Connect Wallet", "userWallet.connectBtn.text")}
          icon={hideInfo && <UserPlus />}
          onClick={handleConnectWallet}
        />
      )}
    </div>
  );
}

export default UserWallet;

interface UserWalletLogoProps {
  walletInfo: IWalletInfo;
}

function UserWalletLogo({ walletInfo }: UserWalletLogoProps) {
  const { wallet } = useWallet();
  if (walletInfo.useSolanaAdapter) {
    if (!wallet?.adapter.icon) {
      return <LogoSolana style={{ height: "16px" }} />;
    }
    return (
      <WalletIcon
        style={{ height: 36 }}
        wallet={{ adapter: { icon: wallet?.adapter.icon, name: wallet?.adapter.name } }}
      />
    );
  }
  const walletKind = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet),
    walletName = walletKind.charAt(0).toUpperCase() + walletKind.slice(1);
  if ("cosmostation" === walletKind) {
    return <LogoCosmostation />;
  }
  if ("keplr" === walletKind) {
    return <LogoKeplr />;
  }
  if ("leap" === walletKind) {
    return <LogoLeap />;
  }
  return walletInfo.walletOwnerName.slice(0, 2);
}
